<template>
  <div class="prog-wrap" ref="wrap">
    <!-- Section header -->
    <div class="prog-header">
      <p class="prog-eyebrow">{{ $t('about.ourCapabilities') }}</p>
      <h2 class="prog-title">{{ $t('about.capabilitiesDesc') }}</h2>
      <div class="prog-title-divider" aria-hidden="true"></div>
    </div>

    <v-row>
      <v-col
        v-for="(skill, index) in skills"
        :key="index"
        cols="6"
        sm="6"
        md="3"
      >
        <div
          class="prog-card"
          :class="{ 'is-visible': inView }"
          :style="{ transitionDelay: `${index * 0.12}s` }"
        >
          <!-- Top accent bar -->
          <div class="prog-card-bar" :style="{ background: skill.gradient }" aria-hidden="true"></div>

          <div class="prog-card-inner">
            <!-- Circular progress ring -->
            <div class="prog-circle-wrap">
              <v-progress-circular
                :rotate="-90"
                :size="120"
                :width="8"
                :value="inView ? skill.value : 0"
                :color="skill.color"
                class="prog-circle"
              >
                <div class="prog-circle-inner">
                  <v-icon size="22" :color="skill.color">{{ skill.icon }}</v-icon>
                  <span class="prog-pct" :style="{ color: skill.color }">
                    {{ displayValues[index] }}%
                  </span>
                </div>
              </v-progress-circular>
            </div>

            <!-- Label & desc -->
            <h3 class="prog-label">{{ $t(skill.label) }}</h3>
            <p class="prog-desc">{{ $t(skill.desc) }}</p>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
// ── Brand palette ─────────────────────────────────────────────────────
$blue:   #3949ab;
$purple: #7c3aed;
$red:    #e53935;

// ── Wrap ──────────────────────────────────────────────────────────────
.prog-wrap {
  padding: 40px 0 60px;
}

// ── Header ────────────────────────────────────────────────────────────
.prog-header {
  text-align: center;
  margin-bottom: 56px;
}

.prog-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: $blue;
  margin-bottom: 12px;

  .theme--dark & { color: lighten($blue, 28%); }

  [dir='rtl'] & {
    letter-spacing: 0;
    text-transform: none;
  }
}

.prog-title {
  font-size: clamp(1.2rem, 2.8vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.4;
  color: #1a1a2e;
  max-width: 580px;
  margin: 0 auto 20px;

  .theme--dark & { color: #eeeef5; }

  [dir='rtl'] & {
    letter-spacing: 0;
    line-height: 1.65;
  }
}

.prog-title-divider {
  width: 48px;
  height: 3px;
  border-radius: 2px;
  margin: 0 auto;
  background: linear-gradient(90deg, $blue, $purple, $red);
}

// ── Card ──────────────────────────────────────────────────────────────
.prog-card {
  height: 100%;
  border-radius: 20px;
  border: 1px solid rgba($blue, 0.1);
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 4px 20px rgba($blue, 0.04);
  overflow: hidden;
  opacity: 0;
  transform: translateY(28px);
  transition:
    opacity 0.65s ease-out,
    transform 0.65s ease-out,
    box-shadow 0.28s ease,
    border-color 0.28s ease;

  .theme--dark & {
    background: rgba(18, 18, 32, 0.88);
    border-color: rgba(255, 255, 255, 0.07);
  }

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 44px rgba($blue, 0.13);
    border-color: rgba($blue, 0.18);

    .theme--dark & { box-shadow: 0 16px 44px rgba(0, 0, 0, 0.32); }
  }
}

// ── Top accent bar ────────────────────────────────────────────────────
.prog-card-bar {
  height: 3px;
  width: 100%;
}

// ── Inner padding ─────────────────────────────────────────────────────
.prog-card-inner {
  padding: 24px 16px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

// ── Circle ────────────────────────────────────────────────────────────
.prog-circle-wrap {
  margin-bottom: 20px;
}

.prog-circle {
  ::v-deep .v-progress-circular__overlay {
    transition: all 1.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ::v-deep .v-progress-circular__underlay {
    stroke: rgba($blue, 0.08);
  }
}

.prog-circle-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.prog-pct {
  font-size: 1rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;

  [dir='rtl'] & { letter-spacing: 0; }
}

// ── Label & desc ──────────────────────────────────────────────────────
.prog-label {
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.35;
  color: #1a1a2e;
  text-align: center;
  margin-bottom: 6px;

  .theme--dark & { color: #eeeef5; }

  [dir='rtl'] & {
    letter-spacing: 0;
    line-height: 1.6;
  }
}

.prog-desc {
  font-size: 0.78rem;
  line-height: 1.6;
  color: #6b6b88;
  text-align: center;
  margin: 0;

  .theme--dark & { color: #9090aa; }

  [dir='rtl'] & { line-height: 1.85; }
}

// ── Responsive ────────────────────────────────────────────────────────
@media (max-width: 600px) {
  .prog-wrap { padding: 24px 0 40px; }
  .prog-header { margin-bottom: 36px; }
  .prog-card:hover { transform: translateY(-3px); }
  .prog-card-inner { padding: 20px 12px 24px; }
}
</style>

<script>
export default {
  data () {
    return {
      inView: false,
      displayValues: [0, 0, 0, 0],
      skills: [
        {
          icon: 'mdi-check-decagram',
          label: 'landing.quality',
          desc: 'about.qualityDesc',
          value: 98,
          color: '#3949ab',
          gradient: 'linear-gradient(135deg, #3949ab, #5c6bc0)'
        },
        {
          icon: 'mdi-headset',
          label: 'landing.support',
          desc: 'about.supportDesc',
          value: 95,
          color: '#7c3aed',
          gradient: 'linear-gradient(135deg, #7c3aed, #9d5bf7)'
        },
        {
          icon: 'mdi-lightbulb-on',
          label: 'landing.innovation',
          desc: 'about.innovationDesc',
          value: 100,
          color: '#e53935',
          gradient: 'linear-gradient(135deg, #7c3aed, #e53935)'
        },
        {
          icon: 'mdi-update',
          label: 'landing.updates',
          desc: 'about.updatesDesc',
          value: 92,
          color: '#f06292',
          gradient: 'linear-gradient(135deg, #e53935, #f06292)'
        }
      ]
    }
  },
  mounted () {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.inView = true
          this.animateCounts()
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )
    observer.observe(this.$refs.wrap)
  },
  methods: {
    animateCounts () {
      const duration = 1400
      const steps = 55
      const intervalMs = duration / steps
      let step = 0
      const timer = setInterval(() => {
        step++
        const progress = step / steps
        this.displayValues = this.skills.map(s => Math.round(s.value * progress))
        if (step >= steps) {
          this.displayValues = this.skills.map(s => s.value)
          clearInterval(timer)
        }
      }, intervalMs)
    }
  }
}
</script>

